import {
  ApiStatus,
  CouchEndpointStatus,
  CouchStatus,
  INetworkConnectionActions,
} from 'shared-types/index';
import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service/index';

const NS = 'NetworkConnectionService';

// do not use for API
const axios: AxiosInstance = NetworkRequestsService.getNewAPIAxiosInstance(false);

export enum NavigatorStatusEvent {
  'online' = 'online',
  'offline' = 'offline',
}

interface ICouchStatusItem {
  localChanges: number;
  connection: CouchStatus;
}

// only used for unit tests, since the navigator object is read only
interface IFakeNavigatorForUnitTests {
  isOnline: boolean;
}

export const LOCAL_STORAGE_POUCH_STATUS = 'pouchStatus';

export const ICON_NAMES = {
  SETUP: 'setup',
  ONLINE: 'online', // @todo: might not be used. Might be able to remove it. severity: low
  OFFLINE: 'offline',
  UP_TO_DATE: 'uptodate',
  SENDING: 'sending',
  RECEIVING: 'receiving',
  SYNCING: 'syncing',
};

class NetworkConnectionService {
  private apiStatus: ApiStatus = ApiStatus.isOnline; // assume this is online until proven otherwise
  private couchEndpointStatus: CouchEndpointStatus = CouchEndpointStatus.isReachable; // assume this is online until proven otherwise
  private currentCouchStatus: ICouchStatusItem = {
    localChanges: 0,
    connection: CouchStatus.setup, // not set to uptodate until either
  };
  private previousCouchConnection: CouchStatus;
  private navigatorListenersAdded = false;

  private delayCouchStatusTimeout: any;

  private fakeNavigatorForUnitTests: IFakeNavigatorForUnitTests;

  private isOfflineDisabled: boolean;

  private couchUrl: string;

  private NetworkConnectionActionsService: INetworkConnectionActions;

  setup(NetworkConnectionActions: INetworkConnectionActions) {
    this.NetworkConnectionActionsService = NetworkConnectionActions;
  }

  setCouchurl(couchUrl: string) {
    const lastSlashIndex = couchUrl.lastIndexOf('/');
    this.couchUrl = couchUrl.substr(0, lastSlashIndex); // just the couchDB url without the db name
  }

  isCouchUrlSet(): boolean {
    return !!this.couchUrl;
  }

  // just used for unit tests to reset values
  __unitTestSetup(): void {
    this.apiStatus = ApiStatus.isOnline;
    this.currentCouchStatus.localChanges = 0;
    this.currentCouchStatus.connection = CouchStatus.setup;
    this.couchEndpointStatus = CouchEndpointStatus.isReachable;
    this.previousCouchConnection = null;
    this.navigatorListenersAdded = false;
    localStorage.setItem(LOCAL_STORAGE_POUCH_STATUS, '');
    this.__setFakeNavigatorForUnitTests(true);
  }

  // only used for unit tests, since the navigator object is read only
  __setFakeNavigatorForUnitTests(isOnline: boolean): void {
    this.fakeNavigatorForUnitTests = { isOnline };
  }

  setApiStatus(status: ApiStatus): void {
    this.apiStatus = status;
    this.NetworkConnectionActionsService.statusChange();
  }

  setCouchEndpointStatus(status: CouchEndpointStatus): void {
    this.couchEndpointStatus = status;
    this.NetworkConnectionActionsService.statusChange();
  }

  setOfflineDisabled(value: boolean): void {
    this.isOfflineDisabled = value;
  }

  getOfflineDisabled(): boolean {
    return this.isOfflineDisabled;
  }

  getCouchStatus(): ICouchStatusItem {
    return this.currentCouchStatus;
  }

  setCouchStatus(connection: CouchStatus): void {
    this.cancelCouchStatusTimeout();
    this.currentCouchStatus.connection = connection;
    this.persistCouchStatusToPouch();
    if (connection !== CouchStatus.offline) {
      this.previousCouchConnection = connection;
    }
    this.NetworkConnectionActionsService.statusChange();
  }

  isOnline(): boolean {
    return (
      this.isNavigatorOnline() &&
      this.isCouchOnline() &&
      this.isApiStatusOnline() &&
      this.isCouchEndpointReachable()
    );
  }

  whyOfflineMessage(): string[] {
    const value = [];
    if (!this.isNavigatorOnline()) {
      value.push('Internet connection is offline.');
    } else {
      if (!this.isCouchEndpointReachable()) value.push('CouchDB is unreachable.');
      if (!this.isCouchOnline()) value.push('CouchDB is reachable, but current status is offline.');
      if (!this.isApiStatusOnline()) value.push('API status is offline.');
    }

    return value;
  }

  getCouchStatusConnection(): CouchStatus {
    return this.currentCouchStatus.connection;
  }

  addNavigatorListeners(): void {
    if (this.navigatorListenersAdded) return;

    this.navigatorListenersAdded = true;
    window.addEventListener(NavigatorStatusEvent.offline, () => this.handleNavigatorOffline());
    window.addEventListener(NavigatorStatusEvent.online, () => this.handleNavigatorOnline());
  }

  addDelayedNavigatorOnlineListener(
    cb: (isOnline: boolean) => void,
    removeOnFirstCase: boolean
  ): void {
    const listener = () => {
      setTimeout(() => cb(this.isOnline()), 1000); // needs delay so that couchStatus has time to get set

      if (removeOnFirstCase) {
        window.removeEventListener(NavigatorStatusEvent.online, listener);
      }
    };

    window.addEventListener(NavigatorStatusEvent.online, listener);
  }

  // doesn't look like we need to remove the listeners
  // removeNavigatorListeners(): void {
  //   window.removeEventListener(NavigatorStatusEvent.offline, this.handleNavigatorOffline);
  //   window.removeEventListener(NavigatorStatusEvent.online, this.handleNavigatorOnline);
  // }

  delayCouchStatus(delay: number, status: CouchStatus): void {
    this.cancelCouchStatusTimeout();
    this.delayCouchStatusTimeout = setTimeout(() => {
      this.setCouchStatus(status);
    }, delay);
  }

  incrementCouchStatusLocalChanges() {
    if (typeof this.currentCouchStatus.localChanges === 'number') {
      this.currentCouchStatus.localChanges++;
    } else {
      this.currentCouchStatus.localChanges = 1;
    }
    this.persistCouchStatusToPouch();
  }

  resetCouchStatusLocalChanges() {
    this.currentCouchStatus.localChanges = 0;
    this.persistCouchStatusToPouch();
  }

  /**
   * Gets a human readable description of status
   */
  getStatusMessage(): string {
    if (!this.isOnline()) {
      return 'Offline';
    }

    switch (this.currentCouchStatus.connection) {
      case CouchStatus.setup:
        return 'Setting up';

      case CouchStatus.sending:
        return 'Uploading';

      case CouchStatus.receiving:
        return 'Downloading';

      case CouchStatus.syncing:
        return 'Syncing';

      case CouchStatus.uptodate:
        return 'Up to date';
    }

    return '';
  }

  /**
   * Matches the name with the ICON_NAMES classes.
   * @param name - These should generally be same as CouchStatus (but could be possibly different in future).
   */
  getNetworkConnectionIconName(name?: string): string {
    if (!this.isOnline()) {
      // no matter what name gets passed, if offline, we show the offline icon
      return ICON_NAMES.OFFLINE;
    }

    if (!name) {
      name = this.currentCouchStatus.connection;
    }
    let result = '';
    for (const prop in ICON_NAMES) {
      const iconName: string = (ICON_NAMES as any)[prop];
      if (iconName === name) {
        result = iconName;
      }
    }
    // if name ever doesn't match CouchStatus then add exceptions here

    if (!result) {
      console.warn(
        NS,
        'getNetworkConnectionIconName',
        `Icon name "${name}" did not match any ICON_NAMES.`
      );
    }

    return result;
  }

  /**
   * Hits the couchDB server to see if any outage has occurred and set offline mode on/off depending on response
   */
  checkCouchDbIsReachable(): Promise<any> {
    return axios
      .get(this.couchUrl, {})
      .then((response: { status: number; data: { couchDb: string } }) => {
        this.setCouchEndpointStatus(
          response.status === 200
            ? CouchEndpointStatus.isReachable
            : CouchEndpointStatus.isUnreachable
        );
        return response;
      })
      .catch((err) => {
        this.setCouchEndpointStatus(CouchEndpointStatus.isUnreachable);
      });
  }

  isApiStatusOnline(): boolean {
    return this.apiStatus === ApiStatus.isOnline;
  }

  isNavigatorOnline(): boolean {
    return this.fakeNavigatorForUnitTests
      ? this.fakeNavigatorForUnitTests.isOnline
      : navigator.onLine;
  }

  private isCouchOnline(): boolean {
    return this.currentCouchStatus.connection !== CouchStatus.offline;
  }

  private isCouchEndpointReachable(): boolean {
    return this.couchEndpointStatus === CouchEndpointStatus.isReachable;
  }

  private cancelCouchStatusTimeout(): void {
    if (this.delayCouchStatusTimeout) {
      clearTimeout(this.delayCouchStatusTimeout);
    }
  }

  private handleNavigatorOffline(): void {
    this.delayCouchStatus(4000, CouchStatus.offline);
    this.NetworkConnectionActionsService.statusChange();
  }

  private handleNavigatorOnline(): void {
    if (this.previousCouchConnection) {
      this.setCouchStatus(this.previousCouchConnection);
    } else {
      this.NetworkConnectionActionsService.statusChange();
    }
  }

  /**
   * Saves couch status to local storage.
   * @todo: this doesn't seem to ever get used. Maybe we should just remove it. Severity: low
   * NB: Used to be saved using $localStorage, which saved as 'ngStorage-pouchStatus' and automatically stringified the object
   */
  private persistCouchStatusToPouch(): void {
    localStorage.setItem(LOCAL_STORAGE_POUCH_STATUS, JSON.stringify(this.currentCouchStatus));
  }
}

const instance = new NetworkConnectionService();
export default instance as NetworkConnectionService;
